// Setup › Who may do what on the SettingsPage pattern (BL-189): the roles at a glance, one section per
// group of what an owner asks about (roles-grid-model.ts), each a DataTable on the role columns; every
// permission one Link away, where the company's own roles change a cell at a time and a role is copied
// (roles.update, roles.clone, the owner's, each asking a fresh second factor: QA-685). Then who holds each
// role, a row each that opens People; for the owner the exceptions for named people (owner-only
// permissions given before roles held them, roles limited to some places); and How people sign in's rules
// (RULE_SECTIONS). Riverstone keeps Settings.tsx's Roles.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, DataTable, IconButton, Label, Link, RowList, type DataColumn } from '@flowology/ui-web/fl';
import { SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useStepUp } from '../shared/StepUpAsk.tsx';
import { reachText, untilText, type Grant, type RoleScope } from './grants-model.ts';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { CloneDialog, NewRoleDialog } from './RoleDialogs.tsx';
import { CELL_WORDS, gridCell, gridFor, type GridCell, type GridRow } from './roles-grid-model.ts';
import { holdersOf, matrixGroups, settingsRights, type MatrixPermission } from './settings-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

interface Role {
  key: string;
  name: string;
  system: boolean;
  permissions: string[];
}
interface Roles {
  roles: Role[];
  permissions: MatrixPermission[];
}
/** People as the roles need them (people.list): who holds which roles. */
interface Holder {
  staffId: string;
  displayName: string;
  roleKeys: string[] | null;
}

const GLANCE_LINE =
  'What each role may do, at a glance. A person may hold several roles and may do what any of them allows. What only the owner gives (costs, margins, payroll) goes in roles only the owner makes and gives. A role of your own is changed from every permission.';
const EVERY_LINE =
  'System roles stay as shipped. Owner only permissions go only in roles the owner makes, and the owner gives those roles. Point at a – to see why it isn’t there.';
/** A glance cell as DESIGN.md §2 draws a state: ✓ ink, – muted, Some strong, Owner dashed (it waits on the owner). */
const glanceWords = (c: GridCell) =>
  c === 'named'
    ? { word: { word: CELL_WORDS[c].mark, tone: 'wait' as const } }
    : c === 'some'
      ? { text: CELL_WORDS[c].mark, look: 'strong' as const }
      : c === 'no'
        ? { text: CELL_WORDS[c].mark, look: 'muted' as const }
        : { text: CELL_WORDS[c].mark };

export function RolesPage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const rights = settingsRights(nav.permissions ?? []);
  const owner = rights.owner;
  const crumb = usePageCrumb(nav);
  const [data, setData] = useState<Roles | null>(null);
  const [error, setError] = useState<string | null>(null);
  // who holds each role (BL-137); null until people.list answers, or when it can't be read here
  const [people, setPeople] = useState<Holder[] | null>(null);
  const [full, setFull] = useState(false);
  const [cloning, setCloning] = useState<{ key: string; name: string } | null>(null);
  const [creating, setCreating] = useState(false);
  // changing a role asks the owner's fresh second factor (QA-685)
  const { run, ask } = useStepUp();
  const load = useCallback(
    () =>
      client
        .call<Roles>('roles.list', {})
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((e: unknown) => setError(refusal(e))),
    [],
  );
  useEffect(() => {
    void load();
    client
      .call<{ people: Holder[] }>('people.list', {})
      .then((r) => setPeople(r.people))
      .catch(() => setPeople(null));
  }, [load]);
  /** One of the company's own roles: the owner turns a permission on or off (roles.update). */
  const toggle = async (role: Role, permission: string) => {
    const on = role.permissions.includes(permission);
    try {
      await run(() =>
        client.call('roles.update', {
          key: role.key,
          permissions: on
            ? role.permissions.filter((x) => x !== permission)
            : [...role.permissions, permission],
        }),
      );
      await load();
    } catch (e) {
      setError(refusal(e));
    }
  };
  const status = data ? 'ready' : error ? 'failed' : 'loading';
  const newRole = owner ? (
    <Button variant="secondary" onPress={() => setCreating(true)}>
      New role
    </Button>
  ) : null;
  const count = data ? matrixGroups(data.permissions).reduce((n, g) => n + g.permissions.length, 0) : 0;
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('roles').label ?? 'Who may do what',
            lede: BP_LEDE.roles,
          }}
          layers={
            <>
              {cloning && (
                <CloneDialog
                  from={cloning}
                  onClose={() => setCloning(null)}
                  onDone={async () => {
                    setCloning(null);
                    await load();
                  }}
                />
              )}
              {creating && (
                <NewRoleDialog
                  onClose={() => setCreating(false)}
                  onDone={async () => {
                    setCreating(false);
                    setFull(true); // a new role starts empty: its permissions are ticked in the full list
                    await load();
                  }}
                />
              )}
              {ask}
            </>
          }
        >
          <SetupRuleSections page="roles" top rights={rights} />
          {full && data ? (
            <EveryPermission
              data={data}
              owner={owner}
              newRole={newRole}
              onBack={() => setFull(false)}
              onClone={setCloning}
              onToggle={(r, p) => void toggle(r, p)}
            />
          ) : (
            <AtAGlance
              data={data}
              status={status}
              error={error}
              onRetry={() => void load()}
              newRole={newRole}
              count={count}
              onFull={() => setFull(true)}
            />
          )}
          {data && people && (
            <SettingsSection
              title="Who has each role"
              line="A person may hold several roles and may do what any of them allows. Change someone’s roles under People."
            >
              <RowList
                label="Who has each role"
                rows={data.roles.map((r) => {
                  const names = holdersOf(r.key, people);
                  return {
                    id: r.key,
                    name: r.name,
                    onOpen: () => nav.onView('settings', 'people'),
                    openLabel: `Change who holds ${r.name} under People`,
                    after: { text: ` · ${names.length ? names.join(', ') : 'Nobody yet'}` },
                  };
                })}
              />
            </SettingsSection>
          )}
          {owner && data && <NamedExceptions roles={data} timeZone={timeZone} />}
          <SetupRuleSections page="roles" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

/** The roles at a glance: one section per group, each row what an owner asks about, a column per role. */
function AtAGlance({
  data,
  status,
  error,
  onRetry,
  newRole,
  count,
  onFull,
}: {
  data: Roles | null;
  status: 'ready' | 'loading' | 'failed';
  error: string | null;
  onRetry: () => void;
  newRole: ReactNode;
  count: number;
  onFull: () => void;
}) {
  const columns: DataColumn<GridRow & { id: string }>[] = data
    ? [
        { id: 'what', label: 'What', rowHeader: true, cell: (row) => ({ text: row.what }) },
        ...data.roles.map((r) => ({
          id: r.key,
          label: r.name,
          cell: (row: GridRow) => glanceWords(gridCell(r, row.keys, data.permissions)),
        })),
      ]
    : [];
  return (
    <>
      <SettingsSection
        title="Roles"
        line={GLANCE_LINE}
        {...(newRole ? { action: newRole } : {})}
        status={status}
        {...(error ? { failedText: error } : {})}
        onRetry={onRetry}
        foot={
          <>
            {(['yes', 'no', 'some', 'named'] as const).map((c) => (
              <span key={c}>
                {CELL_WORDS[c].mark} {CELL_WORDS[c].says}
                {' · '}
              </span>
            ))}
            <Link onPress={onFull}>{`Show every permission (${count})`}</Link>
          </>
        }
      />
      {data &&
        gridFor(data.permissions).map((g) => (
          <SettingsSection key={g.group} title={g.group}>
            <DataTable
              label={g.group}
              layout="fixed"
              columns={columns}
              rows={g.rows.map((row) => ({ ...row, id: row.what }))}
              empty="Nothing here."
            />
          </SettingsSection>
        ))}
    </>
  );
}

/**
 * Every permission, a section per group, a column per role: the company's own roles' cells are the owner's
 * to tick, a click at a time; a system role's, and what stays with the Owner role, read ✓ or –, and point
 * at a – to see why. Each role is copied from its row in Roles.
 */
function EveryPermission({
  data,
  owner,
  newRole,
  onBack,
  onClone,
  onToggle,
}: {
  data: Roles;
  owner: boolean;
  newRole: ReactNode;
  onBack: () => void;
  onClone: (r: { key: string; name: string }) => void;
  onToggle: (r: Role, permission: string) => void;
}) {
  const groups = matrixGroups(data.permissions);
  const system = data.permissions.filter((p) => p.systemOnly);
  const columns: DataColumn<MatrixPermission & { id: string }>[] = [
    {
      id: 'permission',
      label: 'Permission',
      // not the row's head: a head never wraps, and a permission's words would push the roles out of view
      cell: (p) => ({
        text: p.ownerOnly ? (
          <>
            {p.description} <Label kind="wait">Owner only</Label>
          </>
        ) : (
          p.description
        ),
        lines: [{ text: p.key }],
      }),
    },
    ...data.roles.map((r) => ({
      id: r.key,
      label: r.name,
      cell: (p: MatrixPermission) => {
        const on = r.permissions.includes(p.key);
        // what stays with the Owner role goes in no other (BL-137); owner-only ones may
        const kept = p.ownerOnly && p.grantable === false;
        // the company's own roles are the owner's to change, a click at a time
        if (owner && !r.system && !kept && !p.systemOnly)
          return {
            text: (
              // the catalogued toggle: filled in ink when the role holds it; aria-pressed, Space and Enter
              <IconButton
                icon="check"
                label={`${r.name}: ${p.description}`}
                isSelected={on}
                onChange={() => onToggle(r, p.key)}
              />
            ),
          };
        const why = on
          ? undefined
          : kept
            ? 'It stays with the Owner role, in no other.'
            : r.system
              ? `The ${r.name} role is a system role and stays as shipped.`
              : undefined;
        return { text: <span title={why}>{on ? '✓' : '–'}</span>, ...(on ? {} : { look: 'muted' as const }) };
      },
    })),
  ];
  return (
    <>
      <SettingsSection
        title="Roles"
        line={EVERY_LINE}
        {...(newRole ? { action: newRole } : {})}
        foot={<Link onPress={onBack}>Back to who may do what, at a glance</Link>}
      >
        <RowList
          label="Roles"
          rows={data.roles.map((r) => ({
            id: r.key,
            name: r.name,
            lines: [{ text: r.system ? 'Ships with Flowology and stays as shipped' : 'Your own role' }],
            ...(owner && r.key !== 'owner'
              ? {
                  actions: [
                    {
                      id: 'clone',
                      text: 'Clone',
                      label: `Clone the ${r.name} role`,
                      onPress: () => onClone({ key: r.key, name: r.name }),
                    },
                  ],
                }
              : {}),
          }))}
        />
      </SettingsSection>
      {groups.map((g, i) => (
        <SettingsSection
          key={g.title}
          title={g.title}
          {...(i === groups.length - 1 && system.length
            ? {
                foot: `Held by no role, the owner’s included (system handlers, the worker and customer links only): ${system.map((p) => p.key).join(', ')}.`,
              }
            : {})}
        >
          <DataTable
            label={g.title}
            layout="fixed"
            columns={columns}
            rows={g.permissions.map((p) => ({ ...p, id: p.key }))}
            empty="Nothing here."
          />
        </SettingsSection>
      ))}
    </>
  );
}

/**
 * The exceptions to the roles, for named people (the setup audit): the owner-only permissions given to
 * one person before roles held them, and the roles limited to some places. Each is taken back or changed
 * on the person's row under People; a list with nothing in it isn't drawn.
 */
function NamedExceptions({ roles, timeZone }: { roles: Roles; timeZone: string }) {
  const [people, setPeople] = useState<{ staffId: string; displayName: string }[] | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [scopes, setScopes] = useState<RoleScope[]>([]);
  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    void client
      .call<{ people: { staffId: string; displayName: string }[] }>('people.list', {})
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
    void client
      .call<{ grants: Grant[] }>('permissions.listGrants', {})
      .then((r) => setGrants(r.grants))
      .catch(() => setGrants([]));
    void client
      .call<{ scopes: RoleScope[] }>('roles.listScopes', {})
      .then((r) => setScopes(r.scopes))
      .catch(() => setScopes([]));
    void client
      .call<{ businessUnits: { id: string; name: string }[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
  }, []);
  if (!people) return null;
  const who = (id: string) => people.find((p) => p.staffId === id)?.displayName ?? 'Someone';
  const what = (key: string) => roles.permissions.find((p) => p.key === key)?.description ?? key;
  const roleName = (key: string) => roles.roles.find((r) => r.key === key)?.name ?? key;
  const unitName = (id: string) => units.find((u) => u.id === id)?.name ?? 'a department';
  return (
    <>
      {grants.length > 0 && (
        <SettingsSection
          title="Given before roles"
          line="Owner-only permissions given to one person before roles held them. Each runs to its end date; take one back on their row. Nothing new is given this way: give them a role."
        >
          <DataTable
            label="Given permissions"
            columns={[
              { id: 'who', label: 'Person', cell: (g) => ({ text: who(g.staffId) }) },
              {
                id: 'what',
                label: 'Permission',
                cell: (g) => ({ text: <span title={g.permission}>{what(g.permission)}</span> }),
              },
              { id: 'until', label: 'How long', cell: (g) => ({ text: untilText(g.expiresAt, timeZone) }) },
            ]}
            rows={grants.map((g) => ({ ...g, id: `${g.staffId}:${g.permission}` }))}
            empty="Nothing given before roles."
          />
        </SettingsSection>
      )}
      {scopes.length > 0 && (
        <SettingsSection
          title="Roles limited to some places"
          line="Each reaches only the jobs, visits and invoices there; every other role covers the whole company. Change one on the person’s row."
        >
          <DataTable
            label="Roles limited to some places"
            columns={[
              { id: 'who', label: 'Person', cell: (s) => ({ text: who(s.staffId) }) },
              { id: 'role', label: 'Role', cell: (s) => ({ text: roleName(s.roleKey) }) },
              { id: 'reach', label: 'Reaches only', cell: (s) => ({ text: reachText(s, unitName) }) },
            ]}
            rows={scopes.map((s) => ({ ...s, id: `${s.staffId}:${s.roleKey}` }))}
            empty="No role is limited."
          />
        </SettingsSection>
      )}
    </>
  );
}
